import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Spinner } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useTrans } from '../../Store/System/LocalizationStore';
import {
  useCurrentlyActiveCoachPrograms,
  useCurrentlyActiveMindfulnessPrograms,
} from '../../Hooks/useProgramQueries';
import { FitnessProgramCard, MindfulnessProgramCard } from '../../Components/ProgramCard/ProgramCard';
import { Theme } from '../../Theme/Theme';

const Container = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0;
`;

const ProgramCount = styled.span`
  font-size: 12px;
  color: white;
  background: ${Theme.Colors.primary};
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${Theme.Colors.warmGrey};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:not(:first-of-type) {
    margin-top: 20px;
  }
`;

const SectionIcon = styled.span`
  font-size: 14px;
`;

const ProgramList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 24px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  background: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.charcoal};
`;

const EmptyStateIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background: ${Theme.Colors.primary}12;
  color: ${Theme.Colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    width: 26px;
    height: 26px;
    display: block;
  }
`;

const EmptyTitle = styled.h4`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
  max-width: 440px;
`;

const EmptyStateLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  color: ${Theme.Colors.primary};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: ${Theme.Colors.primary};
    text-decoration: none;
  }

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const EmptyProgramsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M3 10h18" />
    <path d="M9 15h6" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

export const ActiveProgramsSection: React.FC = observer(() => {
  const { t } = useTrans();

  // Fetch active programs using hooks
  const { data: fitnessPrograms = [], isLoading: loadingFitness } = useCurrentlyActiveCoachPrograms();
  const { data: mindfulnessPrograms = [], isLoading: loadingMindfulness } = useCurrentlyActiveMindfulnessPrograms();

  const isLoading = loadingFitness || loadingMindfulness;
  const totalPrograms = fitnessPrograms.length + mindfulnessPrograms.length;

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Title>{t('profile.settings.activePrograms', { defaultValue: 'Active Programs' })}</Title>
        </Header>
        <LoadingContainer>
          <Spinner size="sm" color="primary" />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      {totalPrograms > 0 ? (
        <>
        <Header>
          <Title>{t('profile.settings.activePrograms', { defaultValue: 'Active Programs' })}</Title>
          {totalPrograms > 0 && <ProgramCount>{totalPrograms}</ProgramCount>}
        </Header>
          {fitnessPrograms.length > 0 && (
            <>
              <SectionTitle>
                <SectionIcon>💪</SectionIcon>
                {t('runningProgramsModal.fitnessSection', { defaultValue: 'Fitness Programs' })}
              </SectionTitle>
              <ProgramList>
                {fitnessPrograms.map((program) => (
                  <FitnessProgramCard key={program.id} program={program} />
                ))}
              </ProgramList>
            </>
          )}

          {mindfulnessPrograms.length > 0 && (
            <>
              <SectionTitle>
                <SectionIcon>🧘</SectionIcon>
                {t('runningProgramsModal.mindfulnessSection', { defaultValue: 'Mindfulness Programs' })}
              </SectionTitle>
              <ProgramList>
                {mindfulnessPrograms.map((program) => (
                  <MindfulnessProgramCard key={program.id} program={program} />
                ))}
              </ProgramList>
            </>
          )}
        </>
      ) : (
        <EmptyState>
          <EmptyStateIcon>
            <EmptyProgramsIcon />
          </EmptyStateIcon>
          <EmptyTitle>{t('profile.programs.emptyTitle', { defaultValue: 'No active programs yet' })}</EmptyTitle>
          <EmptyText>
            {t('profile.programs.noActive', {
              defaultValue: "You don't have any active programs. Start a program to see your progress here.",
            })}
          </EmptyText>
          <EmptyStateLink to="/move/fitness-programs">
            {t('profile.programs.browseFitnessPrograms', { defaultValue: 'Browse Fitness Programs' })}
            <ArrowRightIcon />
          </EmptyStateLink>
        </EmptyState>
      )}
    </Container>
  );
});
